import React, { useEffect, useState } from 'react';
import { Table, Spin, Alert, Button, Tooltip, Modal, notification } from 'antd';
import { EyeOutlined, EyeInvisibleOutlined, CheckOutlined, CloseOutlined } from '@ant-design/icons';
import { Link } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { getPlatforms, approvePlatform, rejectPlatform } from '../../store/platformActions';
import Header from '@components/Comman/Header';

const PlatformList: React.FC = () => {
  const dispatch = useDispatch();
  const { list, pagination, error } = useSelector((state: any) => state.platforms);
  const [loading, setLoading] = useState(true);
  const [pageSize, setPageSize] = useState(10);
  const [page, setPage] = useState(1);
  const [visibility, setVisibility] = useState<Record<string, { clientIdVisible: boolean; clientSecretVisible: boolean }>>({});

  const toggleVisibility = (id: string, field: 'clientId' | 'clientSecret') => {
    setVisibility(prev => {
      const currentVisibility = prev[id] || { clientIdVisible: false, clientSecretVisible: false };
      const key = field === 'clientId' ? 'clientIdVisible' : 'clientSecretVisible';
      return {
        ...prev,
        [id]: {
          ...currentVisibility,
          [key]: !currentVisibility[key],
        },
      };
    });
  };

  useEffect(() => {
    const fetchPlatforms = async () => {
      try {
        await dispatch(getPlatforms({ page, limit: pageSize }));
      } finally {
        setLoading(false);
      }
    };

    fetchPlatforms();
  }, [dispatch, page, pageSize]);

    const handleApprove = (id: string) => {
        Modal.confirm({
            title: 'Are you sure you want to approve this platform?',
            async onOk() {
                try {
                    const response = await dispatch(approvePlatform(id)) as any;
                    if (response.status === 200) {
                        notification.success({ message: 'Platform approved successfully!' });
                        setLoading(true);
                        await dispatch(getPlatforms({ page, limit: pageSize }));
                        setLoading(false);
                    } else {
                        notification.error({ message: response.message || 'Failed to approve platform.' });
                    }
                } catch (error: any) {
                    notification.error({ message: error.message || 'An unexpected error occurred.' });
                }
            },
            onCancel() { },
        });
    };

    const handleReject = (id: string) => {
        Modal.confirm({
            title: 'Are you sure you want to reject this platform?',
            async onOk() {
                try {
                    const response = await dispatch(rejectPlatform(id)) as any;
                    if (response.status === 200) {
                        notification.success({ message: 'Platform rejected successfully!' });
                        setLoading(true);
                        await dispatch(getPlatforms({ page, limit: pageSize }));
                        setLoading(false);
                    } else {
                        notification.error({ message: response.message || 'Failed to reject platform.' });
                    }
                } catch (error: any) {
                    notification.error({ message: error.message || 'An unexpected error occurred.' });
                }
            },
            onCancel() { },
        });
    };

    const columns = [
    {
      title: 'ID',
      key: 'id',
      render: (text: any, record: any, index: number) => (page - 1) * pageSize + index + 1,
    },
    {
      title: 'Platform Name',
      dataIndex: 'platformName',
      key: 'platformName',
    },
    {
      title: 'Platform URL',
      dataIndex: 'platformURL',
      key: 'platformURL',
    },
    {
      title: 'Client ID',
      dataIndex: 'clientId',
      key: 'clientId',
      render: (text: string, record: any) => (
        <>
          {visibility[record.id]?.clientIdVisible ? text : '********'}
          <Button
            icon={visibility[record.id]?.clientIdVisible ? <EyeInvisibleOutlined /> : <EyeOutlined />}
            onClick={() => toggleVisibility(record.id, 'clientId')}
            type="text"
          />
        </>
      ),
    },
    {
      title: 'Client Secret',
      dataIndex: 'clientSecret',
      key: 'clientSecret',
      render: (text: string, record: any) => (
        <>
          {visibility[record.id]?.clientSecretVisible ? text : '********'}
          <Button
            icon={visibility[record.id]?.clientSecretVisible ? <EyeInvisibleOutlined /> : <EyeOutlined />}
            onClick={() => toggleVisibility(record.id, 'clientSecret')}
            type="text"
          />
        </>
      ),
    },
    {
      title: 'Contact Email',
      dataIndex: 'contactEmail',
      key: 'contactEmail',
    },
    {
      title: 'Contact Person',
      dataIndex: 'contactPerson',
      key: 'contactPerson',
    },
    {
        title: 'Status',
        dataIndex: 'status',
        key: 'status',
    },
    {
        title: 'Actions',
        key: 'actions',
        render: (text: any, record: any) => {
            const isDisabled = record.status !== 'Pending';
            return (
                <span>
                    <Tooltip title="Accept">
                        <Button
                            icon={<CheckOutlined style={{ color: isDisabled ? 'gray' : 'green' }} />}
                            onClick={() => handleApprove(record.id)}
                            type="text"
                            style={{ marginRight: 8 }}
                            disabled={isDisabled}
                        />
                    </Tooltip>
                    <Tooltip title="Reject">
                        <Button
                            icon={<CloseOutlined style={{ color: isDisabled ? 'gray' : 'red' }} />}
                            onClick={() => handleReject(record.id)}
                            type="text"
                            disabled={isDisabled}
                        />
                    </Tooltip>
                </span>
            );
        },
    },
  ];

  return (
    <div>
      <Header pagTitle="Platform List" />
      <div className="contentBody">
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '1rem' }}>
              <Link to="/super-admin/platform-registration">
                <Button style={{
                  backgroundColor: '#F47523',
                  color: 'white',
                  border: 'none',
                  borderRadius: '5px',
                  padding: '6px 15px',
                  fontSize: '14px',
                  height: 'auto'
                }}>Register Platform</Button>
              </Link>
            </div>
            <div className="bodycard">
          <div className="cardContentbody">
            <div className="responsivetable">
              {loading ? (
                <Spin tip="Loading platforms..." />
              ) : error ? (
                <Alert message={error} type="error" />
              ) : (
                <Table 
                  dataSource={list} 
                  columns={columns} 
                  rowKey="id" 
                  pagination={{
                    total: pagination?.totalItems,
                    pageSize: pageSize,
                    current: page,
                    onChange: (page, pageSize) => {
                      setPage(page);
                      setPageSize(pageSize);
                    }
                  }}
                />
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PlatformList;
